import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet, Font } from '@react-pdf/renderer';
import logo from '../../../images/Logo2.png';
import ProximaRegular from '../../../theme/fonts/ProximaNova-Reg.otf';
import ProximaBold from '../../../theme/fonts/ProximaNova-Bold.otf';
import ProximaItalic from '../../../theme/fonts/ProximaNova-RegIt.otf';
import ProximaBoldItalic from '../../../theme/fonts/ProximaNova-BoldIt.otf';
import { isSomething } from '../../../utils/utils';
import { formatSingleDate } from '../planner.utils';

// Register Proxima Nova font family
Font.register({
  family: 'Proxima Nova',
  fonts: [
    { src: ProximaRegular, fontWeight: 'normal' },
    { src: ProximaBold, fontWeight: 'bold' },
    { src: ProximaItalic, fontWeight: 'normal', fontStyle: 'italic' },
    { src: ProximaBoldItalic, fontWeight: 'bold', fontStyle: 'italic' },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Proxima Nova',
    padding: 50,
    paddingBottom: 80,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  font: {
    fontSize: 10,
    lineHeight: '115%',
  },
  font2: {
    fontSize: 10,
    lineHeight: '150%',
  },
  logo: { width: 70 },

  headerRight: {
    textAlign: 'right',
    width: '100%',
  },
  bold: {
    fontFamily: 'Proxima Nova',
    fontWeight: 'bold',
  },
  title: {
    borderBottomWidth: 0.5,
    borderBottomColor: 'black',
    borderBottomStyle: 'solid',
    marginTop: 15,
  },
  borderTop: {
    borderTopWidth: 0.5,
    borderTopColor: 'black',
    borderTopStyle: 'solid',
    paddingTop: 5,
  },

  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 5,
  },
  colLarge: {
    width: '55%',
  },
  deliv: {
    width: '70%',
    paddingTop: 5,
  },
  col30: {
    width: '30%',
    textAlign: 'right',
  },
  col15: {
    width: '15%',
    textAlign: 'right',
  },
  leftIndent: {
    marginLeft: 30,
  },
  text12: { fontSize: 12 },
  divider: {
    borderBottomWidth: 0.5,
    borderBottomColor: 'black',
    borderBottomStyle: 'solid',
    width: '100%',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 50,
    right: 50,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    fontSize: 5,
    height: 50,
    paddingBottom: 5,
    backgroundColor: 'white',
  },
  pageCounter: {
    textAlign: 'right',
    fontSize: 9,
    paddingTop: 5,
  },
});

// Header component

const Header = ({ date }) => (
  <View style={[styles.headerContainer, styles.font]}>
    <View>
      <Image src={logo} style={styles.logo} />
    </View>
    <View style={styles.headerRight}>
      <Text style={styles.text12}>TIMELINE</Text>
      <Text style={[styles.bold]}>{date}</Text>
      <Text style={{ marginTop: 10 }}>FKA</Text>
      <Text>1700, 10123 99 Street</Text>
      <Text>Edmonton, AB T5J 3H1</Text>
      <Text style={{ marginTop: 10 }}>fka.agency | 780 628 5622</Text>
    </View>
  </View>
);

const Footer = () => (
  <View style={styles.footer} fixed>
    <View style={styles.divider} />
    <Text
      style={[styles.pageCounter, styles.bold]}
      render={({ pageNumber, totalPages }) => `${pageNumber}`}
    />
  </View>
);

const TimelinesPDF = ({
  projectData,
  milestones,
  id,
  projectDateRange,
  companyDetails,
  contactName,
}) => {
  const now = new Date();
  const currentDate = now.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Document>
      <Page size="A4" style={[styles.page]}>
        {/* Header */}
        <Header date={currentDate} />

        {/* Client Info */}
        <View style={styles.font}>
          {contactName && <Text style={styles.bold}>Attention: {contactName}</Text>}
          <Text style={styles.bold}>{companyDetails.companyName}</Text>
        </View>

        {/* Project Title & Date Range */}
        <View style={[styles.font2, { paddingTop: 50 }]}>
          <Text style={[styles.text12, styles.bold]}>
            {id} | {projectData.projectName}
          </Text>
          <Text style={[styles.bold, { marginTop: 15 }]}>Timeline: {projectDateRange}</Text>
        </View>

        {/* Timeline Table */}
        <View style={[styles.font2, styles.table]}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.title]}>
            <View style={styles.colLarge}>
              <Text style={styles.bold}>Services</Text>
            </View>
            <View style={[styles.col15, { textAlign: 'left' }]}>
              <Text style={styles.bold}>Responsibility</Text>
            </View>
            <View style={styles.col15}>
              <Text style={styles.bold}>Start</Text>
            </View>
            <View style={styles.col15}>
              <Text style={styles.bold}>End</Text>
            </View>
          </View>

          {/* Milestones & Tasks */}
          {Array.isArray(milestones) &&
            milestones.map((ms, mi) => (
              <View key={`ms-${mi}`}>
                <View style={[styles.tableRow]}>
                  <Text style={[styles.bold, styles.deliv]}>{ms.name}</Text>
                  <Text style={[styles.col30, styles.bold]}>{ms.dateRange}</Text>
                </View>

                {ms.tasks.map((task, ti) => {
                  if (!isSomething(task.startDate) && !isSomething(task.endDate)) return null;
                  return (
                    <View style={styles.tableRow} key={`task-${ti}`}>
                      <View style={styles.colLarge}>
                        <Text style={[styles.leftIndent]}>{task.description}</Text>
                      </View>
                      <View style={[styles.col15, { textAlign: 'left' }]}>
                        <Text>{task.organization}</Text>
                      </View>
                      <View style={styles.col15}>
                        <Text>{formatSingleDate(task.startDate)}</Text>
                      </View>
                      <View style={styles.col15}>
                        <Text>{formatSingleDate(task.endDate)}</Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            ))}
        </View>

        <Footer />
      </Page>
    </Document>
  );
};

export default TimelinesPDF;
